import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ConfirmDialog } from "../components/ConfirmDialog";
import { CredentialDialog } from "../components/CredentialDialog";
import { ApiError, type FamilyApi } from "../lib/api";
import type { Device } from "../lib/types";

interface SettingsPageProps {
  api: FamilyApi;
  offline: boolean;
  memberName: string;
  canManageDevices?: boolean;
  onSignedOut: () => void | Promise<void>;
}

export function SettingsPage({ api, offline, memberName, canManageDevices = true, onSignedOut }: SettingsPageProps) {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [name, setName] = useState("");
  const [credential, setCredential] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Device | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadDevices = useCallback(async () => {
    setError(null);
    try { setDevices((await api.getDevices()).data); }
    catch (caught) { setError(messageFor(caught, "面板清單暫時載入失敗。")); }
  }, [api]);

  useEffect(() => { if (canManageDevices) void loadDevices(); }, [canManageDevices, loadDevices]);

  const createDevice = async (event: FormEvent) => {
    event.preventDefault();
    const normalized = name.trim();
    if (!validDeviceName(normalized)) { setError("面板名稱需為 1 到 80 個字，且不能包含控制字元。"); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      const result = await api.createDevice(normalized);
      setDevices((current) => current ? [...current.filter((item) => item.deviceId !== result.device.deviceId), result.device] : [result.device]);
      setName("");
      if ("credential" in result) setCredential(result.credential);
      else setError("這次請求是重播，原始憑證無法再次顯示。請撤銷這個面板後重新建立。");
    } catch (caught) { setError(messageFor(caught, "無法建立面板憑證，請再試一次。")); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    const target = revokeTarget;
    setBusy(true); setError(null);
    try {
      await api.revokeDevice(target.deviceId, target.version);
      setDevices((current) => current?.filter((item) => item.deviceId !== target.deviceId) ?? null);
      setNotice(`已撤銷「${target.name}」`); setRevokeTarget(null);
    } catch (caught) { setError(messageFor(caught, "無法撤銷面板，請重新整理後再試一次。")); }
    finally { setBusy(false); }
  };

  const signOut = async () => {
    setBusy(true); setError(null);
    try { await onSignedOut(); }
    catch (caught) { setError(messageFor(caught, "登出沒有完成，請再試一次。")); setBusy(false); }
  };

  return (
    <section className="page-stack" aria-labelledby="settings-title">
      <header className="page-heading"><h1 id="settings-title">設定</h1><p>管理家中面板、安裝方式與登入狀態。</p></header>
      {offline && devices ? <p className="stale-note">目前顯示離線資料，設定變更已停用。</p> : null}
      {error ? <div className="notice notice-error" role="alert">{error}<button type="button" onClick={() => void loadDevices()}>重新整理</button></div> : null}
      {notice ? <p className="notice notice-success" role="status">{notice}</p> : null}

      {canManageDevices ? <section className="settings-card" aria-labelledby="panels-heading">
        <div className="settings-heading"><div><p className="eyebrow">ESP32 面板</p><h2 id="panels-heading">已連結的面板</h2></div><span className="count-badge">{devices?.length ?? 0}</span></div>
        <p className="settings-copy">每台面板都有自己的最低權限憑證；撤銷後會立即停止讀取與勾選。</p>
        {!devices ? <div className="device-skeleton" aria-busy="true" aria-label="正在載入面板"><span /><span /></div> : null}
        {devices?.length === 0 ? <p className="settings-empty">尚未建立面板憑證。</p> : null}
        {devices?.length ? <ul className="device-list">{devices.map((device) => <li key={device.deviceId}><div><strong>{device.name}</strong><span>{device.lastSeenAt ? `最近連線 ${relativeTime(device.lastSeenAt)}` : "尚未連線"}</span></div><span className="device-status">可使用</span><button type="button" disabled={offline || busy} onClick={() => setRevokeTarget(device)} aria-label={`撤銷「${device.name}」`}>撤銷</button></li>)}</ul> : null}
        <form className="device-create" onSubmit={(event) => void createDevice(event)}>
          <label className="field"><span>新面板名稱</span><input aria-label="新面板名稱" value={name} maxLength={80} disabled={offline || busy} onChange={(event) => setName(event.currentTarget.value)} placeholder="例如：客廳面板" /></label>
          <button type="submit" className="button button-secondary" disabled={offline || busy || !name.trim()}>{busy ? "建立中…" : "建立面板憑證"}</button>
        </form>
      </section> : null}

      {canManageDevices ? <section className="settings-card" aria-labelledby="panel-controls-heading">
        <p className="eyebrow">面板本機設定</p><h2 id="panel-controls-heading">亮度與 Wi-Fi</h2>
        <p className="settings-copy">Wi-Fi 請在面板底部的「設定」調整。睿寧花園的亮度也可從手機排隊；只有面板確認並保存後才顯示已套用。</p>
        <Link className="button button-secondary" to="/esp">開啟花園控制台</Link>
      </section> : null}

      <section className="settings-card" aria-labelledby="install-heading">
        <p className="eyebrow">iPhone</p><h2 id="install-heading">加入主畫面</h2>
        <ol className="install-steps"><li>用 Safari 打開這個網站</li><li>點工具列的「分享」</li><li>選「加入主畫面」</li></ol>
        <p className="settings-copy">之後可像 App 一樣從主畫面開啟；登入仍由安全的伺服器工作階段恢復。</p>
      </section>

      <section className="settings-card account-row" aria-labelledby="account-heading">
        <div><p className="eyebrow">Google 帳號</p><h2 id="account-heading">{memberName}</h2><p className="settings-copy">Google 帳號只用來確認身分，不會把家庭待辦寫入 Google Tasks。</p></div>
        <button type="button" className="button button-ghost" disabled={busy} onClick={() => void signOut()}>登出</button>
      </section>

      {credential ? <CredentialDialog credential={credential} onClose={() => setCredential(null)} /> : null}
      {revokeTarget ? <ConfirmDialog title="撤銷面板？" description={`「${revokeTarget.name}」會立即停止同步，之後若要重連必須建立新憑證。`} confirmLabel="確認撤銷" destructive busy={busy} onCancel={() => setRevokeTarget(null)} onConfirm={() => void revoke()} /> : null}
    </section>
  );
}

function validDeviceName(value: string) { return Array.from(value).length >= 1 && Array.from(value).length <= 80 && new TextEncoder().encode(value).length <= 256 && !/[\u0000-\u001f\u007f-\u009f]/u.test(value); }
function relativeTime(timestamp: string) { const minutes = Math.max(0, Math.floor((Date.now() - Date.parse(timestamp)) / 60_000)); if (minutes < 1) return "剛剛"; if (minutes < 60) return `${minutes} 分鐘前`; const hours = Math.floor(minutes / 60); if (hours < 24) return `${hours} 小時前`; return `${Math.floor(hours / 24)} 天前`; }
function messageFor(error: unknown, fallback: string) { return error instanceof ApiError ? error.message : fallback; }
